import React, { useState } from "react";
import { toast } from "sonner";
import PageHero from "../components/blocks/PageHero";
import PremiumButton from "../components/ui/PremiumButton";
import { feedbackAPI } from "../services/api";
import useSEO from "../hooks/useSEO";

const Feedback = () => {
  useSEO('/feedback');
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    stayDate: "",
    rating: 5,
    accommodation: "",
    feedback: "",
    suggestions: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await feedbackAPI.submit({
        name: formData.name,
        email: formData.email,
        stayDate: formData.stayDate || null,
        rating: Number(formData.rating),
        accommodation: formData.accommodation || null,
        feedback: formData.feedback,
        suggestions: formData.suggestions || null,
      });

      toast.success("Thank you for your feedback — we truly appreciate it.");
      setFormData({
        name: "",
        email: "",
        stayDate: "",
        rating: 5,
        accommodation: "",
        feedback: "",
        suggestions: "",
      });
    } catch (e) {
      toast.error("Could not submit right now. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050706] text-[#F4F1E8] pt-16">
      <PageHero
        title="Feedback"
        subtitle="Help us grow and improve"
        image="https://images.unsplash.com/photo-1470549584009-d347338fc0ff?auto=format&fit=crop&w=2400&q=80"
        heightClass="h-[52vh]"
      />

      {/* Introduction */}
      <section className="py-16">
        <div className="mx-auto max-w-6xl px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-light mb-6 text-white">
              Your Voice Matters
            </h2>
            <p className="text-sm md:text-base text-white/70 leading-relaxed">
              At Glow Lawns, we're constantly learning and evolving. Your honest feedback—whether it's praise, constructive criticism, or suggestions for improvement—helps us create better experiences for future guests and stay true to our mission.
            </p>
          </div>
        </div>
      </section>

      {/* Feedback Form */}
      <section className="py-16">
        <div className="mx-auto max-w-6xl px-4">
          <div className="max-w-3xl mx-auto">
            <div className="rounded-2xl border border-white/10 bg-white/5 p-8 backdrop-blur-md md:p-12">
              <form onSubmit={handleSubmit}>
                {/* Name & Email */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-white mb-2">
                      Your Name <span className="text-[#C9A227]">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-[#C9A227]/50"
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-white mb-2">
                      Email Address <span className="text-[#C9A227]">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-[#C9A227]/50"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                {/* Stay Date & Rating */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="stayDate" className="block text-sm font-medium text-white mb-2">
                      Date of Stay
                    </label>
                    <input
                      type="date"
                      id="stayDate"
                      name="stayDate"
                      value={formData.stayDate}
                      onChange={handleChange}
                      className="w-full rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-[#C9A227]/50"
                    />
                  </div>
                  <div>
                    <label htmlFor="rating" className="block text-sm font-medium text-white mb-2">
                      Overall Rating <span className="text-[#C9A227]">*</span>
                    </label>
                    <select
                      id="rating"
                      name="rating"
                      value={formData.rating}
                      onChange={handleChange}
                      required
                      className="w-full rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#C9A227]/50"
                    >
                      <option value="5">⭐⭐⭐⭐⭐ Excellent</option>
                      <option value="4">⭐⭐⭐⭐ Good</option>
                      <option value="3">⭐⭐⭐ Average</option>
                      <option value="2">⭐⭐ Below Average</option>
                      <option value="1">⭐ Poor</option>
                    </select>
                  </div>
                </div>

                {/* Accommodation */}
                <div className="mb-6">
                  <label htmlFor="accommodation" className="block text-sm font-medium text-white mb-2">
                    Which Accommodation Did You Stay In?
                  </label>
                  <select
                    id="accommodation"
                    name="accommodation"
                    value={formData.accommodation}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#C9A227]/50"
                  >
                    <option value="">Select...</option>
                    <option value="MoonCanopy">MoonCanopy (Tree House)</option>
                    <option value="Forest Whisper">Forest Whisper (Tree House)</option>
                    <option value="Silver Grove">Silver Grove (Tree House)</option>
                    <option value="Valley Light">Valley Light (Tree House)</option>
                    <option value="EarthNest">EarthNest (Mud House)</option>
                  </select>
                </div>

                {/* Feedback */}
                <div className="mb-6">
                  <label htmlFor="feedback" className="block text-sm font-medium text-white mb-2">
                    Your Feedback <span className="text-[#C9A227]">*</span>
                  </label>
                  <textarea
                    id="feedback"
                    name="feedback"
                    value={formData.feedback}
                    onChange={handleChange}
                    required
                    rows="6"
                    className="w-full rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-[#C9A227]/50 resize-none"
                    placeholder="Share your experience—what did you love? What could we improve? Be as honest as you'd like."
                  ></textarea>
                </div>

                {/* Suggestions */}
                <div className="mb-6">
                  <label htmlFor="suggestions" className="block text-sm font-medium text-white mb-2">
                    Suggestions for Improvement
                  </label>
                  <textarea
                    id="suggestions"
                    name="suggestions"
                    value={formData.suggestions}
                    onChange={handleChange}
                    rows="4"
                    className="w-full rounded-xl border border-white/10 bg-black/30 px-4 py-3 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-[#C9A227]/50 resize-none"
                    placeholder="Any ideas or suggestions on how we can make Glow Lawns even better?"
                  ></textarea>
                </div>

                {/* Submit Button */}
                <PremiumButton
                  variant="primary"
                  icon={false}
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full justify-center py-4"
                >
                  {isSubmitting ? "Submitting…" : "Submit Feedback"}
                </PremiumButton>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Why Feedback Matters */}
      <section className="py-16">
        <div className="mx-auto max-w-6xl px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-light mb-8 text-stone-900 text-center">
              How We Use Your Feedback
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="bg-emerald-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">💚</span>
                </div>
                <h3 className="text-lg font-semibold mb-2 text-stone-900">Improve Experiences</h3>
                <p className="text-sm text-stone-600">
                  Your suggestions directly shape our service improvements
                </p>
              </div>

              <div className="text-center">
                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🌱</span>
                </div>
                <h3 className="text-lg font-semibold mb-2 text-stone-900">Train Our Team</h3>
                <p className="text-sm text-stone-600">
                  We share feedback with staff to celebrate wins and address gaps
                </p>
              </div>

              <div className="text-center">
                <div className="bg-amber-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">✨</span>
                </div>
                <h3 className="text-lg font-semibold mb-2 text-stone-900">Future Planning</h3>
                <p className="text-sm text-stone-600">
                  Long-term improvements are prioritized based on guest input
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Feedback;
